import React, { useState } from 'react';
import { BookOpen, Castle, ChevronRight, Home, Users, Scroll, BookmarkCheck } from 'lucide-react';
import { RobertsPart1Section } from './roberts/RobertsPart1Section';
import { RobertsPart2Section } from './roberts/RobertsPart2Section';
import { RobertsPart3Section } from './roberts/RobertsPart3Section';
import { RobertsPart4Section } from './roberts/RobertsPart4Section';
import { RobertsPart5Section } from './roberts/RobertsPart5Section';

export interface RobertsPartItem {
  id: string;
  partNumber: number;
  title: string;
  subtitle: string;
  isAvailable: boolean;
}

export const ROBERTS_PARTS: RobertsPartItem[] = [
  {
    id: 'part1',
    partNumber: 1,
    title: 'The Roberts Family in Wales',
    subtitle: 'Hawarden Castle & Gladstone, Penmaenmawr, Patronymic Naming, Welsh Ancestral Homes & Skylight at Cabbage Tree Flat',
    isAvailable: true,
  },
  {
    id: 'part2',
    partNumber: 2,
    title: 'The Roberts Family: Large, Literate, Literary and Hardworking',
    subtitle: 'Cabinet maker, seaman, road builder, gold miner, bridge maker, storekeeper, inn keeper, hotel operator, drapery salesman, railway construction worker, farm worker, farmer',
    isAvailable: true,
  },
  {
    id: 'part3',
    partNumber: 3,
    title: 'Ted (Edward) Roberts (1881-1975) and his family',
    subtitle: 'Riverside at Culverden, Romance & Tragedies, Edward T. Roberts, and Over a Century of Change in Farming Practice',
    isAvailable: true,
  },
  {
    id: 'part4',
    partNumber: 4,
    title: 'The Bike Ride to the Ballot at Starborough & T.E.L. Roberts',
    subtitle: '"The buoyancy of youth was in that ride, with a new world opening out." Lonely Grave, Beyond the Hills of Hundalee',
    isAvailable: true,
  },
  {
    id: 'part5',
    partNumber: 5,
    title: 'Robert Morris Roberts - Troqueer & Family History',
    subtitle: 'From Motunau childhood and Happy Valley shearing to Starborough, Blind River, Troqueer Farm, and authentic archival correspondence.',
    isAvailable: true,
  },
];

export const RobertsSection: React.FC = () => {
  const [activePartId, setActivePartId] = useState<string>('part5');

  const currentPart = ROBERTS_PARTS.find(p => p.id === activePartId) || ROBERTS_PARTS[0];

  const handleSelectPage = (pageId: string) => {
    if (pageId === 'hub') {
      setActivePartId('all');
    } else {
      setActivePartId(pageId);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-16 font-sans text-[#2c221e]">
      
      {/* 5-Part Series Navigation Bar */}
      <nav aria-label="Roberts Family Series Navigation" className="bg-[#f3ede0] p-3 sm:p-4 rounded-2xl border-2 border-[#d8ceb9] shadow-xs">
        <div className="flex items-center justify-between gap-2 pb-2.5 mb-2.5 border-b border-[#d8ceb9] text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
            <Castle className="w-4 h-4 text-[#7a5b3a]" />
            <span>The Roberts Family: 5-Part Historical Series</span>
          </div>
          <button
            onClick={() => setActivePartId(activePartId === 'all' ? 'part3' : 'all')}
            className="text-[11px] font-bold text-[#7a5b3a] hover:underline bg-[#e8dec9] px-2.5 py-1 rounded-lg transition cursor-pointer"
          >
            {activePartId === 'all' ? 'View Single Part' : 'View All Parts'}
          </button>
        </div>

        {/* Horizontal Part Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {ROBERTS_PARTS.map((part) => {
            const isSelected = activePartId === part.id;
            return (
              <button
                key={part.id}
                onClick={() => {
                  setActivePartId(part.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`whitespace-nowrap px-3.5 py-2 rounded-xl text-xs font-serif font-bold transition flex items-center gap-2 shrink-0 border cursor-pointer ${
                  isSelected
                    ? 'bg-[#7a5b3a] text-white border-[#7a5b3a] shadow-xs'
                    : 'bg-[#fbf9f4] text-[#4a3525] border-[#d8ceb9] hover:bg-[#e8dec9]'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                  isSelected ? 'bg-amber-200 text-[#2c221e]' : 'bg-[#e8dec9] text-[#7a5b3a]'
                }`}>
                  {part.partNumber}
                </span>
                <span>
                  Part {part.partNumber}
                  {part.partNumber === 1 ? ': Wales' : part.partNumber === 2 ? ': Literate & Hardworking' : part.partNumber === 3 ? ': Ted & Riverside' : part.partNumber === 4 ? ': Starborough & T.E.L.' : part.partNumber === 5 ? ': Troqueer' : ''}
                </span>
                {part.isAvailable && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Published" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* RENDER ACTIVE PART */}
      {activePartId === 'part1' && (
        <RobertsPart1Section onSelectPage={handleSelectPage} />
      )}

      {activePartId === 'part2' && (
        <RobertsPart2Section onSelectPage={handleSelectPage} />
      )}

      {activePartId === 'part3' && (
        <RobertsPart3Section onSelectPage={handleSelectPage} />
      )}

      {activePartId === 'part4' && (
        <RobertsPart4Section onSelectPage={handleSelectPage} />
      )}

      {activePartId === 'part5' && (
        <RobertsPart5Section onSelectPage={handleSelectPage} />
      )}

      {/* ALL PARTS VIEW / HUB */}
      {activePartId === 'all' && (
        <div className="space-y-12">
          {/* Chapter Index Hub Cards */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-6">
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#2c221e] flex items-center gap-2">
                <Castle className="w-6 h-6 text-[#7a5b3a]" />
                <span>The Roberts Family: 5-Part Series Index</span>
              </h2>
              <p className="text-sm text-[#7a5b3a] mt-1">
                Select a part below to explore individual chapters or scroll down to view published sections.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ROBERTS_PARTS.map((part) => (
                <div
                  key={part.id}
                  onClick={() => {
                    setActivePartId(part.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-[#fbf9f4] p-5 rounded-2xl border border-[#d8ceb9] hover:border-[#7a5b3a] transition cursor-pointer space-y-2 group shadow-xs hover:shadow-md relative"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-serif font-bold uppercase tracking-wider text-[#7a5b3a] bg-[#e8dec9] px-2.5 py-0.5 rounded-full">
                      Part {part.partNumber} of 5
                    </span>
                    {part.isAvailable ? (
                      <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                        Published
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-[#7a5b3a] bg-[#f3ede0] px-2 py-0.5 rounded-full">
                        Upcoming
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-lg font-bold text-[#2c221e] group-hover:text-[#7a5b3a] transition-colors">
                    {part.title}
                  </h3>
                  <p className="text-xs text-[#5c4328] line-clamp-2">
                    {part.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          {/* Render All 5 Parts in All View */}
          <div className="space-y-12">
            <RobertsPart1Section onSelectPage={handleSelectPage} />
            <hr className="border-t-4 border-dashed border-[#d8ceb9]" />
            <RobertsPart2Section onSelectPage={handleSelectPage} />
            <hr className="border-t-4 border-dashed border-[#d8ceb9]" />
            <RobertsPart3Section onSelectPage={handleSelectPage} />
            <hr className="border-t-4 border-dashed border-[#d8ceb9]" />
            <RobertsPart4Section onSelectPage={handleSelectPage} />
            <hr className="border-t-4 border-dashed border-[#d8ceb9]" />
            <RobertsPart5Section onSelectPage={handleSelectPage} />
          </div>
        </div>
      )}

    </div>
  );
};
